import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { OnboardingStepHeader } from "@/features/onboarding/components/onboarding-step-header";
import { useEffect, useState, type ReactNode } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Calendar, Settings } from "lucide-react";
import { usePhoneInput } from "react-international-phone";
import { sendPhoneVerification } from "../../ajax/phone/sendPhoneVerification";
import { verifyPhoneCode } from "../../ajax/phone/verifyPhoneCode";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { validatePhoneNumber } from "../../ajax/phone/validatePhoneNumber";
import Loading from "../Loading";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type PhoneFormData = {
  phoneNumber: string;
  consent: boolean;
};

/** A "why we need your phone number" bullet. */
export type PhoneReason = {
  icon: ReactNode;
  text: string;
};

const DEFAULT_PHONE_REASONS: PhoneReason[] = [
  {
    icon: <Bell className="h-5 w-5" />,
    text: "Get notified when your credentials are about to expire",
  },
  {
    icon: <Calendar className="h-5 w-5" />,
    text: "Receive updates about jobs you've applied to through the app",
  },
  {
    icon: <Settings className="h-5 w-5" />,
    text: "We never send spam - you can adjust your message settings anytime",
  },
];

type VerificationFormData = {
  verificationCode: string;
};

async function formValidatePhoneNumber(phoneNumber: string) {
  const { valid } = await validatePhoneNumber({
    phoneNumber,
  });
  return valid || "Phone number is not valid";
}

export default function PhoneForm({
  onComplete,
  defaultPhoneNumber = "",
  reasons = DEFAULT_PHONE_REASONS,
  confirmSkip = false,
}: {
  onComplete: () => void;
  /**
   * Prefills the phone input (e.g. a provider's number already on file). The
   * consent checkbox is deliberately NOT prefilled — the user must still opt
   * in to texts, and verification still runs.
   */
  defaultPhoneNumber?: string;
  /** "Why we need your phone number" bullets; varies by provider audience. */
  reasons?: PhoneReason[];
  /**
   * When true, "Skip" opens a confirmation dialog urging the user to add a
   * number (important updates come by text) and requires them to type
   * "I understand" before continuing. Used for org-affiliated providers.
   */
  confirmSkip?: boolean;
}) {
  const [showVerification, setShowVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [hasResent, setHasResent] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipConfirmText, setSkipConfirmText] = useState("");
  const canConfirmSkip =
    skipConfirmText.trim().toLowerCase() === "i understand";

  const phoneForm = useForm<PhoneFormData>({
    defaultValues: {
      phoneNumber: defaultPhoneNumber,
      consent: false,
    },
  });

  const verificationForm = useForm<VerificationFormData>({
    reValidateMode: "onChange",
    defaultValues: {
      verificationCode: "",
    },
  });

  const { inputValue, handlePhoneValueChange } = usePhoneInput({
    defaultCountry: "us",
    disableDialCodeAndPrefix: true,
    value: phoneForm.watch("phoneNumber"),
    onChange: ({ phone }) => {
      phoneForm.setValue("phoneNumber", phone, {
        shouldValidate: phoneForm.formState.isSubmitted,
      });
    },
  });

  const onPhoneSubmit = async (data: PhoneFormData) => {
    if (!data.phoneNumber || !data.consent) {
      setError("Please enter your phone number and agree to the terms.");
      return;
    }

    const result = await sendPhoneVerification({
      phoneNumber: data.phoneNumber,
    });

    if (result.error) {
      setError(result.error);
      return;
    }

    setPhoneNumber(data.phoneNumber);
    setShowVerification(true);
    setError("");
  };

  const onVerificationSubmit = async (data: VerificationFormData) => {
    const result = await verifyPhoneCode({
      phoneNumber: phoneNumber,
      code: data.verificationCode,
    });

    if (result.error) {
      setError(result.error);
      setShowVerification(false);
      verificationForm.setValue("verificationCode", "");
      return;
    }

    setError("");
    await handleNext();
  };

  useEffect(() => {
    const verificationCode = verificationForm.watch("verificationCode");
    if (verificationCode.length === 6) {
      verificationForm.handleSubmit(onVerificationSubmit)();
    }
  }, [verificationForm.watch("verificationCode")]);

  const handleNext = async () => {
    onComplete();
  };

  const handleResendCode = async () => {
    if (hasResent) {
      return;
    }

    const result = await sendPhoneVerification({
      phoneNumber: phoneNumber,
    });

    if (result.error) {
      setError(result.error);
      setShowResendDialog(false);
      return;
    }

    setHasResent(true);
    setSuccess("Verification code resent successfully.");
    setShowResendDialog(false);
  };

  const handleGoBack = () => {
    setShowVerification(false);
    setShowResendDialog(false);
    verificationForm.reset();
  };

  phoneForm.register("phoneNumber", {
    required: true,
    validate: {
      checkPhoneNumber: formValidatePhoneNumber,
    },
  });

  return (
    <>
      {showVerification ? (
        <div>
          <OnboardingStepHeader
            title="Phone Verification"
            description="Enter the six-digit code we sent to your phone to finish setting up text notifications."
          />
          <form
            onSubmit={verificationForm.handleSubmit(onVerificationSubmit)}
            className="space-y-5"
          >
            <div className={ONBOARDING_FORM_STYLES.section}>
              <p className="mb-1 text-sm text-slate-500">
                Verification code sent to
              </p>
              <p className="mb-5 text-lg font-semibold text-slate-900">
                {inputValue}
              </p>
              <div>
                <Controller
                  name="verificationCode"
                  control={verificationForm.control}
                  rules={{
                    required: "Verification code is required",
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: "Must be a 6-digit number",
                    },
                  }}
                  render={({ field }) => (
                    <InputOTP
                      autoFocus
                      {...field}
                      maxLength={6}
                      aria-label="Verification code"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot
                          index={0}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                        <InputOTPSlot
                          index={1}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                        <InputOTPSlot
                          index={2}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                        <InputOTPSlot
                          index={3}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                        <InputOTPSlot
                          index={4}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                        <InputOTPSlot
                          index={5}
                          className="h-12 w-8 border-slate-400 text-lg sm:w-10 lg:h-12 lg:w-10"
                        />
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
              </div>
              {verificationForm.formState.errors.verificationCode && (
                <p role="alert" className="mt-2 text-sm text-red-700">
                  {verificationForm.formState.errors.verificationCode.message}
                </p>
              )}
              {error && (
                <p role="alert" className={ONBOARDING_FORM_STYLES.error}>
                  {error}
                </p>
              )}
              {success && (
                <p role="status" className="mt-3 text-sm text-emerald-700">
                  {success}
                </p>
              )}
            </div>
            <div className={ONBOARDING_FORM_STYLES.footer}>
              <Button
                disabled={verificationForm.formState.isSubmitting}
                size="none"
                type="submit"
                className={ONBOARDING_FORM_STYLES.primaryButton}
              >
                {verificationForm.formState.isSubmitting ? (
                  <Loading className="h-6 w-6" />
                ) : (
                  "Verify"
                )}
              </Button>
              <div className="flex items-center justify-center sm:order-first">
                <Button
                  className="min-h-12 px-0"
                  type="button"
                  variant="link"
                  onClick={() => setShowResendDialog(true)}
                >
                  Didn't receive the code?
                </Button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div>
          <OnboardingStepHeader
            title="Enter Phone Number"
            description="Stay up to date with important reminders and account updates by text."
          />
          <section className="mb-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6">
            <h3 className={ONBOARDING_FORM_STYLES.sectionHeading}>
              Updates that matter
            </h3>
            <ul className="mt-4 space-y-4">
              {reasons.map((reason, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary"
                  >
                    {reason.icon}
                  </span>
                  <span className="pt-1 text-sm leading-6 text-slate-600">
                    {reason.text}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <form
            onSubmit={phoneForm.handleSubmit(onPhoneSubmit)}
            className="space-y-5"
          >
            <div className={ONBOARDING_FORM_STYLES.section}>
              <Label
                className="mb-3 block text-sm font-medium text-slate-700"
                htmlFor="phoneNumber"
              >
                Phone Number
              </Label>
              <Controller
                name="phoneNumber"
                control={phoneForm.control}
                rules={{ required: "Phone number is required" }}
                render={({ field }) => (
                  <Input
                    id="phoneNumber"
                    className={ONBOARDING_FORM_STYLES.input}
                    onBlur={field.onBlur}
                    aria-invalid={!!phoneForm.formState.errors.phoneNumber}
                    type="tel"
                    placeholder="(000) 000-0000"
                    value={inputValue}
                    onChange={handlePhoneValueChange}
                    inputMode="tel"
                    autoComplete="tel"
                    ref={field.ref}
                    autoFocus
                  />
                )}
              />
              {phoneForm.formState.errors.phoneNumber && (
                <p role="alert" className="mt-2 text-sm text-red-700">
                  {phoneForm.formState.errors.phoneNumber.message}
                </p>
              )}
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <Controller
                name="consent"
                control={phoneForm.control}
                rules={{ required: "You must agree to the terms" }}
                render={({ field }) => (
                  <Checkbox
                    id="consent"
                    className="mt-1 shrink-0"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label
                htmlFor="consent"
                className="cursor-pointer text-sm font-normal leading-6 text-slate-600"
              >
                By providing your phone number and pressing "Save" button below,
                you agree to receive automated text messages from Saile App Inc.
                Consent is not a condition for purchase. Message and data rates
                may apply.
              </Label>
            </div>
            {phoneForm.formState.errors.consent && (
              <p role="alert" className="text-sm text-red-700">
                {phoneForm.formState.errors.consent.message}
              </p>
            )}
            {error && (
              <p role="alert" className={ONBOARDING_FORM_STYLES.error}>
                {error}
              </p>
            )}

            <div className={ONBOARDING_FORM_STYLES.footer}>
              <Button
                size="none"
                disabled={phoneForm.formState.isSubmitting}
                type="submit"
                className={ONBOARDING_FORM_STYLES.primaryButton}
              >
                {phoneForm.formState.isSubmitting ? (
                  <Loading className="h-6 w-6" />
                ) : (
                  "Save"
                )}
              </Button>
              <div className="sm:order-first">
                <Button
                  className={ONBOARDING_FORM_STYLES.secondaryButton}
                  onClick={() =>
                    confirmSkip ? setShowSkipDialog(true) : handleNext()
                  }
                  type="button"
                  variant="ghost"
                >
                  Skip
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
      <Dialog
        open={showSkipDialog}
        onOpenChange={(open) => {
          setShowSkipDialog(open);
          if (!open) {
            setSkipConfirmText("");
          }
        }}
      >
        <DialogContent className={ONBOARDING_FORM_STYLES.confirmation}>
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl">
              Add your phone number?
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              Important updates come to your phone — reminders about your
              credentials, your job scheduling and upcoming shifts, and messages
              about getting work and receiving your payments. Without a phone
              number you may miss them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="skip-confirm" className="text-sm">
              To continue without a phone number, type{" "}
              <span className="font-semibold">I understand</span>.
            </Label>
            <Input
              id="skip-confirm"
              className={ONBOARDING_FORM_STYLES.input}
              value={skipConfirmText}
              onChange={(e) => setSkipConfirmText(e.target.value)}
              placeholder="I understand"
              autoComplete="off"
            />
          </div>
          <DialogFooter className="gap-3 pt-2 sm:flex-col sm:space-x-0 lg:flex-col">
            <Button
              size="lg"
              className={`${ONBOARDING_FORM_STYLES.primaryButton} h-auto min-h-12 whitespace-normal py-3 sm:w-full`}
              onClick={() => {
                setShowSkipDialog(false);
                setSkipConfirmText("");
              }}
            >
              Go back and add my number
            </Button>
            <Button
              variant="ghost"
              className={`${ONBOARDING_FORM_STYLES.secondaryButton} h-auto min-h-12 whitespace-normal py-3 sm:w-full`}
              disabled={!canConfirmSkip}
              onClick={() => {
                setShowSkipDialog(false);
                setSkipConfirmText("");
                handleNext();
              }}
            >
              Continue without a phone number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <DialogContent className={ONBOARDING_FORM_STYLES.confirmation}>
          <DialogHeader>
            <DialogTitle>Didn't receive the code?</DialogTitle>
            <DialogDescription>
              You can choose to resend the code or go back to enter a new phone
              number.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={handleGoBack}
              variant="outline"
              className={ONBOARDING_FORM_STYLES.secondaryButton}
            >
              Go Back
            </Button>
            <Button
              onClick={handleResendCode}
              disabled={hasResent}
              className={ONBOARDING_FORM_STYLES.primaryButton}
            >
              {hasResent ? "Code Resent" : "Resend Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
